import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Receipt, Plus, Send, Trash2, Copy, DollarSign } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';

const STATUS_COLORS = {
  draft: 'bg-slate-200 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-rose-100 text-rose-700',
  void: 'bg-slate-100 text-slate-500 line-through',
};

const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function Invoices() {
  const { user } = useAuth();
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientFilter = searchParams.get('client_id');

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);

  const canManage = canEdit(user, 'invoices');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, cRes, eRes] = await Promise.all([
        api.get('/invoices', { params: { client_id: clientFilter || undefined } }),
        api.get('/clients'),
        api.get('/events'),
      ]);
      setInvoices(iRes.data || []);
      setClients(cRes.data || []);
      setEvents(eRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load');
    } finally {
      setLoading(false);
    }
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);

  if (invoiceId) {
    return <InvoiceEditor invoiceId={invoiceId} clients={clients} events={events} canManage={canManage} onClose={() => { navigate('/invoices'); load(); }} />;
  }

  const create = async () => {
    if (clients.length === 0) {
      toast.error('Create a client first');
      navigate('/clients');
      return;
    }
    try {
      const r = await api.post('/invoices', { client_id: clientFilter || clients[0].id, items: [] });
      navigate(`/invoices/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create');
    }
  };

  const totalOpen = invoices.filter((i) => ['sent', 'partial', 'overdue'].includes(i.status)).reduce((s, i) => s + (i.balance || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + (i.paid || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Receipt className="w-7 h-7 text-emerald-600" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Invoices</h1>
                <p className="text-sm text-slate-500">Bill clients for work delivered. Track payments and outstanding balances.</p>
              </div>
            </div>
            {canManage && <Button onClick={create}><Plus className="w-4 h-4 mr-1" /> New invoice</Button>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Card className="glass-card p-4">
              <p className="text-xs text-slate-500">Outstanding</p>
              <p className="text-2xl font-bold text-amber-700">{fmtMoney(totalOpen)}</p>
            </Card>
            <Card className="glass-card p-4">
              <p className="text-xs text-slate-500">Paid (all-time)</p>
              <p className="text-2xl font-bold text-emerald-700">{fmtMoney(totalPaid)}</p>
            </Card>
            <Card className="glass-card p-4">
              <p className="text-xs text-slate-500">Total invoices</p>
              <p className="text-2xl font-bold text-slate-800">{invoices.length}</p>
            </Card>
          </div>

          {loading ? <p className="text-center py-8 text-slate-500">Loading…</p> : invoices.length === 0 ? (
            <Card className="glass-card p-12 text-center text-slate-500"><Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />No invoices yet.</Card>
          ) : (
            <Card className="glass-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-xs text-slate-600 text-left">
                  <tr><th className="px-4 py-2">Number</th><th className="px-4 py-2">Client</th><th className="px-4 py-2">Issue</th><th className="px-4 py-2">Due</th><th className="px-4 py-2">Total</th><th className="px-4 py-2">Paid</th><th className="px-4 py-2">Balance</th><th className="px-4 py-2">Status</th><th></th></tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs">{i.number}</td>
                      <td className="px-4 py-2">{clients.find((c) => c.id === i.client_id)?.name || '—'}</td>
                      <td className="px-4 py-2 text-xs">{i.issue_date}</td>
                      <td className="px-4 py-2 text-xs">{i.due_date}</td>
                      <td className="px-4 py-2 font-semibold">{fmtMoney(i.total, i.currency)}</td>
                      <td className="px-4 py-2 text-emerald-700">{fmtMoney(i.paid, i.currency)}</td>
                      <td className="px-4 py-2 text-amber-700 font-medium">{fmtMoney(i.balance, i.currency)}</td>
                      <td className="px-4 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[i.status] || 'bg-slate-100'}`}>{i.status}</span></td>
                      <td className="px-4 py-2 text-right"><Link to={`/invoices/${i.id}`} className="text-emerald-600 text-xs">Open →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

function InvoiceEditor({ invoiceId, clients, events, canManage, onClose }) {
  const [invoice, setInvoice] = useState(null);
  const [photographers, setPhotographers] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, paid_at: new Date().toISOString().slice(0, 10), method: 'bank', reference: '', note: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, pRes] = await Promise.all([
        api.get(`/invoices/${invoiceId}`),
        api.get('/photographers'),
      ]);
      setInvoice(iRes.data);
      setPhotographers(pRes.data || []);
      // If invoice has event_id, pull approved timesheets to suggest line items
      if (iRes.data.event_id) {
        const ts = await api.get('/timesheets', { params: { event_id: iRes.data.event_id, status: 'approved' } });
        setTimesheets(ts.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [invoiceId, onClose]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setInvoice((i) => ({ ...i, ...patch }));
  const updateItem = (idx, patch) => setInvoice((i) => ({ ...i, items: i.items.map((it, idx2) => idx2 === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setInvoice((i) => ({ ...i, items: [...(i.items || []), { id: crypto.randomUUID(), description: '', quantity: 1, unit: 'flat', unit_price: 0 }] }));
  const removeItem = (idx) => setInvoice((i) => ({ ...i, items: i.items.filter((_, idx2) => idx2 !== idx) }));

  const importTimesheets = () => {
    if (!timesheets.length) {
      toast.error('No approved timesheets for this event');
      return;
    }
    // Group by photographer + bill_rate
    const groups = {};
    for (const t of timesheets) {
      if (t.invoice_id) continue; // already invoiced
      const key = `${t.photographer_id}::${t.bill_rate || 0}`;
      if (!groups[key]) {
        groups[key] = { name: t.photographer_name, rate: t.bill_rate || 0, hours: 0, photographer_id: t.photographer_id };
      }
      groups[key].hours += (t.hours || 0);
    }
    const newItems = Object.values(groups).map((g) => ({
      id: crypto.randomUUID(),
      description: `${g.name} — photography hours`,
      quantity: parseFloat(g.hours.toFixed(2)),
      unit: 'hour',
      unit_price: g.rate,
      photographer_id: g.photographer_id,
    }));
    if (newItems.length === 0) {
      toast.error('All approved timesheets are already on an invoice');
      return;
    }
    setInvoice((i) => ({ ...i, items: [...(i.items || []), ...newItems] }));
    toast.success(`Imported ${newItems.length} line${newItems.length === 1 ? '' : 's'} from timesheets`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/invoices/${invoiceId}`, {
        title: invoice.title, intro_html: invoice.intro_html,
        items: invoice.items, currency: invoice.currency,
        tax_rate: invoice.tax_rate, discount: invoice.discount,
        issue_date: invoice.issue_date, due_date: invoice.due_date,
        notes: invoice.notes, status: invoice.status,
      });
      setInvoice(r.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!window.confirm('Mark as sent and copy share link?')) return;
    try {
      const r = await api.post(`/invoices/${invoiceId}/send`);
      setInvoice(r.data);
      const link = `${window.location.origin}${r.data.share_url}`;
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Sent · link copied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    }
  };

  const copyLink = () => {
    if (!invoice.public_token) return;
    const link = `${window.location.origin}/share/invoice/${invoice.public_token}`;
    navigator.clipboard.writeText(link).catch(() => {});
    toast.success('Link copied');
  };

  const addPayment = async () => {
    if (paymentForm.amount <= 0) {
      toast.error('Amount must be positive');
      return;
    }
    try {
      const r = await api.post(`/invoices/${invoiceId}/payments`, paymentForm);
      setInvoice(r.data);
      setPaymentForm({ amount: 0, paid_at: new Date().toISOString().slice(0, 10), method: 'bank', reference: '', note: '' });
      toast.success('Payment recorded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record payment');
    }
  };

  const removePayment = async (paymentId) => {
    if (!window.confirm('Remove this payment?')) return;
    try {
      const r = await api.delete(`/invoices/${invoiceId}/payments/${paymentId}`);
      setInvoice(r.data);
      toast.success('Payment removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  if (loading || !invoice) return <div className="min-h-screen bg-slate-50"><Sidebar /><main className="md:ml-64 p-8"><p>Loading…</p></main></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <Button variant="ghost" size="sm" onClick={onClose}>← Back</Button>
              <h1 className="text-2xl font-bold mt-2">Invoice <span className="font-mono text-emerald-600">{invoice.number}</span>
                <span className={`ml-3 text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[invoice.status] || 'bg-slate-100'}`}>{invoice.status}</span>
              </h1>
            </div>
            {canManage && (
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                {invoice.status === 'draft' && <Button size="sm" onClick={send}><Send className="w-3.5 h-3.5 mr-1" /> Mark sent</Button>}
                {invoice.public_token && <Button variant="outline" size="sm" onClick={copyLink}><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>}
                <Button variant="ghost" size="sm" onClick={remove} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>

          <Card className="glass-card p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Client</Label>
                <Select value={invoice.client_id} onValueChange={(v) => update({ client_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event</Label>
                <Select value={invoice.event_id || ''} onValueChange={(v) => update({ event_id: v || null })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="">— None —</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Issue date</Label>
                <Input type="date" value={invoice.issue_date} onChange={(e) => update({ issue_date: e.target.value })} />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" value={invoice.due_date} onChange={(e) => update({ due_date: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Input value={invoice.currency} onChange={(e) => update({ currency: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>Tax rate</Label>
                <Input type="number" step="0.01" value={invoice.tax_rate} onChange={(e) => update({ tax_rate: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="md:col-span-3">
                <Label>Title</Label>
                <Input value={invoice.title || ''} onChange={(e) => update({ title: e.target.value })} />
              </div>
            </div>
          </Card>

          <Card className="glass-card p-5 mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-semibold">Line items</h2>
              <div className="flex gap-2 flex-wrap">
                {timesheets.length > 0 && <Button size="sm" variant="outline" onClick={importTimesheets}>Import from timesheets</Button>}
                <Button size="sm" variant="outline" onClick={addItem}><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 border-b">
                <tr>
                  <th className="py-1.5 pr-2">Description</th>
                  <th className="py-1.5 pr-2 w-24">Qty</th>
                  <th className="py-1.5 pr-2 w-28">Unit</th>
                  <th className="py-1.5 pr-2 w-32">Unit price</th>
                  <th className="py-1.5 pr-2 w-28 text-right">Total</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((it, i) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2"><Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} className="h-8" /></td>
                    <td className="py-1.5 pr-2"><Input type="number" step="0.5" value={it.quantity} onChange={(e) => updateItem(i, { quantity: parseFloat(e.target.value) || 0 })} className="h-8" /></td>
                    <td className="py-1.5 pr-2">
                      <Select value={it.unit || 'flat'} onValueChange={(v) => updateItem(i, { unit: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="flat">flat</SelectItem><SelectItem value="hour">hour</SelectItem><SelectItem value="day">day</SelectItem></SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 pr-2"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: parseFloat(e.target.value) || 0 })} className="h-8" /></td>
                    <td className="py-1.5 pr-2 text-right font-medium">{fmtMoney((it.quantity || 0) * (it.unit_price || 0), invoice.currency)}</td>
                    <td className="py-1.5"><button onClick={() => removeItem(i)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
                {(invoice.items || []).length === 0 && <tr><td colSpan={6} className="text-center py-3 text-slate-400 text-xs">No line items</td></tr>}
              </tbody>
            </table>

            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
              <div className="flex gap-3"><span className="text-slate-500">Subtotal:</span><span className="font-medium w-28 text-right">{fmtMoney(invoice.subtotal, invoice.currency)}</span></div>
              <div className="flex gap-3 items-center"><span className="text-slate-500">Discount:</span><Input type="number" step="0.01" value={invoice.discount} onChange={(e) => update({ discount: parseFloat(e.target.value) || 0 })} className="h-7 w-24 text-right" /></div>
              <div className="flex gap-3"><span className="text-slate-500">Tax:</span><span className="font-medium w-28 text-right">{fmtMoney(invoice.tax, invoice.currency)}</span></div>
              <div className="flex gap-3 text-base"><span className="font-semibold">Total:</span><span className="font-bold w-28 text-right">{fmtMoney(invoice.total, invoice.currency)}</span></div>
              <div className="flex gap-3 text-emerald-700"><span>Paid:</span><span className="font-medium w-28 text-right">{fmtMoney(invoice.paid, invoice.currency)}</span></div>
              <div className="flex gap-3 text-amber-700"><span>Balance:</span><span className="font-bold w-28 text-right">{fmtMoney(invoice.balance, invoice.currency)}</span></div>
            </div>
          </Card>

          <Card className="glass-card p-5">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Payments</h2>
            {(invoice.payments || []).length > 0 && (
              <table className="w-full text-sm mb-4">
                <thead className="text-xs text-slate-500 text-left border-b"><tr><th className="py-1.5">Date</th><th>Amount</th><th>Method</th><th>Ref</th><th>Note</th><th></th></tr></thead>
                <tbody>
                  {invoice.payments.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="py-1.5">{p.paid_at}</td>
                      <td className="font-medium">{fmtMoney(p.amount, invoice.currency)}</td>
                      <td>{p.method}</td>
                      <td className="text-xs text-slate-500">{p.reference}</td>
                      <td className="text-xs text-slate-500">{p.note}</td>
                      <td><button onClick={() => removePayment(p.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {canManage && invoice.status !== 'paid' && (
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end bg-slate-50 p-3 rounded">
                <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={paymentForm.paid_at} onChange={(e) => setPaymentForm({ ...paymentForm, paid_at: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Method</Label>
                  <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="bank">Bank</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-xs">Reference</Label><Input value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} placeholder="Bank ref / receipt #" /></div>
                <Button size="sm" onClick={addPayment}>Record</Button>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
